import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Send, Trash2, KeyRound, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { formatWIB } from "../../lib/datetime";

export default function OrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);

  const load = () => api.get(`/admin/orders/${id}`).then((r) => setOrder(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const assign = async () => {
    try { await api.post(`/admin/orders/${id}/assign-key`); toast.success("Key assigned"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const resend = async () => {
    try { const r = await api.post(`/admin/orders/${id}/resend`); toast.success(r.data.sent ? "Email sent" : "Email queue may be unavailable"); }
    catch { toast.error("Failed"); }
  };
  const markComplete = async () => {
    await api.put(`/admin/orders/${id}/status`, { order_status: "completed" });
    toast.success("Marked completed"); load();
  };
  const del = async () => {
    if (!window.confirm("Delete this order?")) return;
    await api.delete(`/admin/orders/${id}`);
    toast.success("Deleted");
    window.location.href = "/admin/orders";
  };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  if (!order) return <div className="text-[#A1A1AA] font-mono">loading...</div>;

  return (
    <div data-testid="order-detail-page">
      <Link to="/admin/orders" className="font-mono text-xs text-[#A1A1AA] hover:text-[#00FF66] inline-flex items-center gap-1 mb-6">
        <ArrowLeft className="w-3.5 h-3.5" />back to orders
      </Link>

      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="font-heading font-bold text-3xl font-mono text-[#00FF66]">{order.id}</div>
          <div className="text-[#A1A1AA] text-sm mt-1">{order.email} · {formatWIB(order.created_at)}</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={assign} className="btn-ghost" data-testid="admin-assign-key"><KeyRound className="w-3.5 h-3.5" />assign key</button>
          <button onClick={resend} className="btn-ghost" data-testid="admin-resend"><Send className="w-3.5 h-3.5" />resend email</button>
          <button onClick={markComplete} className="btn-ghost" data-testid="admin-mark-complete">mark completed</button>
          <button onClick={del} className="btn-ghost hover:!border-[#FF3B30] hover:!text-[#FF3B30]" data-testid="admin-delete-order"><Trash2 className="w-3.5 h-3.5" />delete</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="surface p-6">
            <h3 className="font-heading font-semibold mb-4">Items</h3>
            <table className="w-full text-sm">
              <tbody>
                {order.items.map((it, i) => (
                  <tr key={i} className="border-b border-[#1E293B] last:border-0">
                    <td className="py-2">{it.name}</td>
                    <td className="py-2 font-mono text-right">× {it.quantity}</td>
                    <td className="py-2 font-mono text-right">${(it.price * it.quantity).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="surface p-6">
            <h3 className="font-heading font-semibold mb-4">License keys ({order.license_keys?.length || 0})</h3>
            {order.license_keys?.length ? (
              <div className="space-y-2">
                {order.license_keys.map((k, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 surface p-3">
                    <div>
                      <div className="font-mono text-[10px] text-[#A1A1AA] uppercase tracking-widest">{k.product_name}</div>
                      <code className="font-mono text-[#00FF66] text-sm break-all">{k.key}</code>
                    </div>
                    <button onClick={() => copy(k.key)} className="btn-ghost shrink-0"><Copy className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-[#A1A1AA] font-mono text-sm">no keys delivered yet</div>
            )}
          </div>
        </div>

        <div>
          <div className="surface p-6">
            <h3 className="font-heading font-semibold mb-4">Summary</h3>
            <div className="space-y-2 font-mono text-sm">
              <Row k="subtotal" v={`$${order.subtotal?.toFixed(2)}`} />
              {order.discount > 0 && <Row k="discount" v={`-$${order.discount.toFixed(2)}`} />}
              {order.coupon_code && <Row k="coupon" v={order.coupon_code} />}
              <div className="border-t border-[#1E293B] pt-2 mt-2 flex justify-between font-bold">
                <span>total</span><span className="text-[#00FF66]">${order.total.toFixed(2)}</span>
              </div>
            </div>
            <div className="mt-5 pt-5 border-t border-[#1E293B] space-y-3">
              <div className="flex items-center justify-between"><span className="text-[#A1A1AA] text-xs font-mono">payment</span><span className="chip-mono">{order.payment_status}</span></div>
              <div className="flex items-center justify-between"><span className="text-[#A1A1AA] text-xs font-mono">status</span><span className="chip-mono">{order.order_status}</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between"><span className="text-[#A1A1AA]">{k}</span><span>{v}</span></div>;
}
